import { Grid, Card, Typography, Divider, Avatar, Stack } from '@mui/material';
import {
  IPayslips,
  getAccessiblePayslipFile,
  getPayslipFile,
} from 'API/Paychecks/paychecks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { Download2, PurpleCircleBG, WarningOutlineIcon } from 'Images';
import { downloadFile, numberToMonthText } from 'Utils';

interface ICardPaycheck {
  cardData: IPayslips;
  isActive?: boolean;
}
export const CardPaycheck = ({ cardData, isActive }: ICardPaycheck) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Paychecks);
  const isDesktop = useIsDesktop();
  const { isLoading, setIsLoading, loader } = useLoader(
    undefined,
    { height: 40 },
    isDesktop ? 50 : 35,
    false
  );

  const watchPdf = async (accessibilityNeeded?: boolean) => {
    setIsLoading(true);

    const file = accessibilityNeeded
      ? (await getAccessiblePayslipFile(cardData.accessibleFileId))?.file
      : (await getPayslipFile(cardData.fileId))?.file;

    if (file) downloadFile(file, 'pdf', dictionary.paychecksFileName);

    setIsLoading(false);
  };

  return (
    <Card
      sx={{
        background: '#FFFFFF',
        display: 'flex',
        border: isActive ? '1px solid' : undefined,
        borderColor: 'purple2.shade_300',
        boxShadow: '0px 4px 20px rgba(45, 112, 217, 0.12)',
        borderRadius: '16px',
        minHeight: isDesktop ? '138px' : '116px',
        padding: isDesktop ? '24px 32px' : '16px 24px',
      }}
    >
      <Grid item xs={3}>
        <Grid
          container
          direction='column'
          alignItems={'center'}
          gap={isDesktop ? '8px' : '4px'}
          justifyContent={'space-between'}
          sx={{
            background: `url(${PurpleCircleBG}) no-repeat left bottom`,
            backgroundPositionX: '8%',
          }}
        >
          <Typography variant='body6' component='h3'>
            {dictionary.yearHe} {cardData.year}
          </Typography>
          <Typography
            fontSize={{ sm: '64px', xs: '64px' }}
            lineHeight={{ sm: '60px', xs: '60px' }}
            fontWeight={{ sm: '600', xs: '600' }}
          >
            {parseInt(cardData.month)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={1}>
        <Divider
          aria-hidden={'true'}
          orientation='vertical'
          sx={{ borderColor: 'primary.shade_100' }}
        />
      </Grid>
      <Grid item xs={8}>
        <Grid
          container
          direction='column'
          pl={'22px'}
          gap={'8px'}
          justifyContent={'space-between'}
        >
          <Typography variant='body6'>
            {numberToMonthText(parseInt(cardData.month))}
          </Typography>

          {isLoading ? (
            loader
          ) : (
            <>
              {cardData.fileId && (
                <Stack
                  flexDirection={'row'}
                  mb={1}
                  alignItems={'center'}
                  sx={{ cursor: 'pointer' }}
                  component={'a'}
                  tabIndex={0}
                  onClick={() => watchPdf()}
                  onKeyUp={(e: React.KeyboardEvent) => {
                    e.key === 'Enter' && watchPdf();
                  }}
                  aria-label={`${dictionary.slip} ${numberToMonthText(
                    parseInt(cardData.month)
                  )} ${cardData.year}`}
                >
                  <Avatar
                    src={Download2}
                    alt={''}
                    sx={{
                      width: '20px',
                      height: '20px',
                      mr: 1,
                    }}
                  />
                  <Typography variant='body5' color='primary.main'>
                    {dictionary.downloadPaycheck}
                  </Typography>
                </Stack>
              )}
              {cardData.accessibleFileId ? (
                <Stack
                  flexDirection={'row'}
                  alignItems={'center'}
                  sx={{ cursor: 'pointer' }}
                  component={'a'}
                  tabIndex={0}
                  onClick={() => watchPdf(true)}
                  onKeyUp={(e: React.KeyboardEvent) => {
                    e.key === 'Enter' && watchPdf(true);
                  }}
                >
                  <Avatar
                    src={Download2}
                    alt={''}
                    sx={{
                      width: '20px',
                      height: '20px',
                      mr: 1,
                    }}
                  />
                  <Typography variant='body5' color='primary.main'>
                    {dictionary.watchAccessibilityFile}
                  </Typography>
                </Stack>
              ) : (
                //Accessible files available only from 03/2017
                parseInt(cardData.year) <= 2017 &&
                (parseInt(cardData.year) === 2017
                  ? parseInt(cardData.month) < 3
                  : true) && (
                  <Stack
                    flexDirection={'row'}
                    alignItems={'flex-start'}
                    sx={{ cursor: 'pointer' }}
                    component={'a'}
                    tabIndex={0}
                  >
                    <Avatar
                      src={WarningOutlineIcon}
                      alt={''}
                      sx={{
                        width: '16px',
                        height: '16px',
                        mr: 1,
                        top: '3px',
                      }}
                    />
                    <Typography
                      variant='body5'
                      fontSize={{ sm: '16px' }}
                      color='text.primary'
                    >
                      {dictionary.accessibilityFileNotAvailable}
                    </Typography>
                  </Stack>
                )
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};
